/**
 * /es/transcribir-entrevista — interview-transcription reframe bridge (ES).
 *
 * Collapses the big ES interview long-tail (como transcribir una entrevista,
 * transcribir entrevistas, transcripción de entrevistas…). Neutral Spanish.
 */
import { MarketingShell } from './MarketingShell';

export function EsTranscribirEntrevista() {
  return (
    <MarketingShell
      currentLocale="es"
      hreflangPath="/transcribir-entrevista"
      hreflangLocales={['es']}
      documentTitle="Cómo transcribir una entrevista (gratis) y sacarle partido · Draftlize"
      documentDescription="Cómo transcribir una entrevista paso a paso y gratis, con ejemplo y plantilla. Y lo que casi nadie hace: convertir el verbatim en decisiones de producto."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guía · Entrevistas</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Cómo transcribir una entrevista<br />
            <em>— y qué hacer después.</em>
          </h1>
          <p className="lede">
            Subes el audio y, en segundos, tienes una transcripción limpia. Las herramientas de transcripción — Notta, Otter, tl;dv — son excelentes capturando lo que se dijo. Sigue usándolas. Pero el verbatim es <em>materia prima</em>, no el entregable. Lo que de verdad importaba de esa entrevista — el insight y la <em>decisión de producto</em> que provocó — se pierde entre miles de palabras, acaba en un archivo que nadie reabre y queda obsoleto en semanas. Esta página trata del paso siguiente.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Transcribir una entrevista, gratis.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Consigue el audio</strong> — grabación de la llamada, nota de voz o MP3.</li>
            <li><strong>Transcribe</strong> — una herramienta de IA convierte voz en texto, normalmente con cuota gratuita.</li>
            <li><strong>Revisa</strong> — corrige nombres propios y exporta el verbatim.</li>
          </ol>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '22px 0 0' }}>
            Ya tienes la transcripción. ¿Y ahora? Ahí se detiene el 90 % del trabajo — y se evapora el valor de la entrevista. Capturar ya no es el cuello de botella. <em>Extraer</em> lo es.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Materia prima vs contexto vivo</span>
            <h2 className="serif">Donde acaba la transcripción, <em>empieza el trabajo.</em></h2>
          </div>
          <p>Tu herramienta de transcripción y Draftlize hacen trabajos distintos. Una captura la entrevista; la otra mantiene vivas las decisiones que salen de ella. Quédate con las dos.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>Herramienta de transcripción</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Función</td><td>Capturar lo que se dijo</td><td className="ours yes">Mantener al día lo que se decidió</td></tr>
            <tr><td>Salida</td><td className="partial">Verbatim + resumen</td><td className="ours yes">Tarjetas de decisión en un grafo</td></tr>
            <tr><td>Un insight cambia una decisión</td><td className="no">Dos notas sin relación</td><td className="ours yes">Enlazadas; la antigua se marca «stale»</td></tr>
            <tr><td>Tres semanas después</td><td className="no">Enterrado en un archivo</td><td className="ours yes">Localizable, al día o señalado</td></tr>
            <tr><td>Un agente de IA puede usarlo</td><td className="partial">Lee texto plano</td><td className="ours yes">Lee y escribe vía MCP</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">De la entrevista a la decisión de producto.</h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Deja la captura a tu herramienta. Lo que importa es qué pasa con las pocas decisiones que salen de tus entrevistas — en Draftlize dejan de ser líneas en un verbatim y se convierten en contexto de producto que sigue siendo cierto. ¿Tomas notas de reuniones con IA? Mismo principio: <a href="/es/ia-para-tomar-notas-de-reuniones" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>IA para notas de reuniones</a>.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Las decisiones se vuelven tarjetas</h3>
            <p>Pega el resumen de la entrevista y las decisiones que contiene se convierten en tarjetas con tipo — la decisión, su justificación, la cita que la respalda y el responsable. La hora de entrevista se reduce a lo que debe sobrevivirla.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Conecta el verbatim con el requisito</h3>
            <p>Cada cita de usuario queda enlazada con la decisión que provocó y el requisito que de ella deriva. Cuando una entrevista posterior contradice el insight, lo que depende de él se marca «stale».</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Tu agente de IA lee lo decidido</h3>
            <p>Las decisiones de tus entrevistas se vuelven contexto que Claude Code o Cursor leen vía MCP — el agente que redacta el siguiente spec ya sabe qué dijeron los usuarios y qué concluiste.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Tu herramienta transcribe la entrevista a la perfección. Lo que no puede decirte, tres semanas después, es qué decisiones de las que salieron siguen en pie.
        </blockquote>
        <cite>Quédate con la transcripción. Deja que Draftlize mantenga vivas las decisiones.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Preguntas frecuentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '¿Cómo transcribo una entrevista gratis?', a: 'La mayoría de herramientas (Notta, tl;dv, Otter) ofrecen cuota gratuita. Draftlize no transcribe: actúa una vez tienes el verbatim, para extraer las decisiones y mantenerlas al día.' },
            { q: '¿Qué formato uso para una transcripción?', a: 'Texto plano con marcas de tiempo basta para una entrevista de usuario. Lo importante no es el formato del verbatim, sino qué haces con él después.' },
            { q: '¿Qué hago con la transcripción una vez la tengo?', a: 'Pega el resumen en Draftlize y captura las decisiones como tarjetas — decisión, justificación, cita de apoyo. No se trata de guardar todo el verbatim, sino de mantener localizables las pocas decisiones que importan.' },
            { q: '¿En qué se diferencia de guardar notas en una carpeta?', a: 'En una carpeta las decisiones quedan como texto plano, desconectado de los specs que afectan. Envejecen en silencio. Draftlize las enlaza y señala lo que queda obsoleto cuando cambias de idea.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Tus entrevistas merecen algo mejor que un .txt olvidado <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Regístrate y recibe $5</span>
          <span>Paga solo por lo que usas</span>
          <span>El saldo nunca caduca</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Quédate con tu herramienta de transcripción. Suelta el resumen en Draftlize y las decisiones de tus entrevistas se vuelven tarjetas vivas — enlazadas, al día y legibles por tu agente de IA.
          </p>
          <a className="btn-primary" href="/">Empieza gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
